import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaTrash,
  FaShieldAlt,
  FaPlus,
  FaMinus,
  FaEdit,
  FaTruck,
  FaInfoCircle,
} from "react-icons/fa";
import axios from "axios";
import { Helmet } from "react-helmet";

// Assumed Component Imports
import Header from "./header";
import Footer from "./Footer";

// Assumed Service Imports
import {
  fetchCart,
  removeCartItem,
  updateCartQuantity,
} from "../services/UserService";
import { getLoggedUser } from "../controllers/AuthController";
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=600&q=80";
// --- CONFIGURATION ---
const API_BASE_URL = `${process.env.REACT_APP_API_URL}`;
// const RAZORPAY_KEY_ID = "rzp_test_2iWnEO9Cz1mzNV";
const RAZORPAY_KEY_ID = "rzp_live_4umYUOdEa9fo1h";

// --- THEME CONSTANTS ---
const THEME_COLOR = "#0f172a";
const THEME_LIGHT = "#f1f5f9";

// Responsive styles function
const getResponsiveStyles = (width = null) => {
  const screenWidth = width !== null ? width : window.innerWidth;

  // Breakpoint definitions based on requested ranges:
  // 320–487, 488–588, 589–748, 749–1040, 1041–1250, 1251–1550, 1551–1940, 1941+
  const isRange320_487 = screenWidth >= 320 && screenWidth < 488;
  const isRange488_588 = screenWidth >= 488 && screenWidth < 589;
  const isRange589_748 = screenWidth >= 589 && screenWidth < 749;
  const isRange749_1040 = screenWidth >= 749 && screenWidth < 1041;
  const isRange1041_1250 = screenWidth >= 1041 && screenWidth < 1251;
  const isRange1251_1550 = screenWidth >= 1251 && screenWidth < 1551;
  const isRange1551_1940 = screenWidth >= 1551 && screenWidth < 1941;
  const isRange1940_plus = screenWidth >= 1941;

  // Combined view checks (mobile / tablet / desktop)
  const isMobileView = screenWidth < 749; // 320–748
  const isTabletView = screenWidth >= 749 && screenWidth < 1041; // 749–1040
  const isDesktopView = screenWidth >= 1041; // 1041+

  // Specific range for 320px–487px (hide header, show cards list)
  const isFirstMobileRange = isRange320_487;

  return {
    page: {
      backgroundColor: "#f8fafc",
      minHeight: "100vh",
      paddingBottom: isRange320_487
        ? "40px"
        : isRange488_588
          ? "48px"
          : isRange589_748
            ? "56px"
            : "60px",
      fontFamily: "'Inter', sans-serif",
    },
    container: {
      maxWidth: isMobileView
        ? "100%"
        : isTabletView
          ? "100%"
          : isRange1041_1250
            ? "1100px"
            : isRange1251_1550
              ? "1200px"
              : isRange1551_1940
                ? "1400px"
                : "1600px",
      margin: "0 auto",
      padding: isRange320_487
        ? "12px 10px"
        : isRange488_588
          ? "16px 12px"
          : isRange589_748
            ? "20px 16px"
            : isTabletView
              ? "24px 18px"
              : "30px 20px",
    },
    headerRow: {
      display: isFirstMobileRange ? "none" : "flex",
      flexDirection: isMobileView ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobileView ? "flex-start" : "center",
      gap: isMobileView ? "16px" : "0",
      marginBottom: isRange320_487 ? "16px" : isRange488_588 ? "20px" : "24px",
    },
    title: {
      fontSize: isRange320_487
        ? "16px"
        : isRange488_588
          ? "18px"
          : isRange589_748
            ? "20px"
            : isTabletView
              ? "22px"
              : "24px",
      fontWeight: 700,
      color: "#0f172a",
      display: "flex",
      alignItems: "center",
      gap: isRange320_487 ? "6px" : "10px",
    },
    backLink: {
      fontSize: isRange320_487 ? "12px" : isMobileView ? "13px" : "14px",
      color: THEME_COLOR,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      fontWeight: 600,
    },
    // Stepper
    stepperContainer: {
      display: isMobileView ? "none" : "flex",
      gap: isTabletView ? "6px" : "10px",
      backgroundColor: "#fff",
      padding: isTabletView ? "8px 16px" : "10px 20px",
      borderRadius: "30px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      flexWrap: isTabletView ? "wrap" : "nowrap",
    },
    stepItem: (status) => ({
      display: "flex",
      alignItems: "center",
      gap: isTabletView ? "4px" : "8px",
      padding: isTabletView ? "4px 10px" : "6px 16px",
      borderRadius: "20px",
      fontSize: isTabletView ? "11px" : isDesktopView ? "12px" : "13px",
      fontWeight: 600,
      backgroundColor:
        status === "active"
          ? THEME_COLOR
          : status === "completed"
            ? "#10b981"
            : "transparent",
      color: status === "pending" ? "#94a3b8" : "#fff",
      transition: "all 0.3s ease",
    }),
    layout: {
      display: isMobileView ? "block" : "grid",
      gridTemplateColumns: isTabletView
        ? "1fr 320px"
        : isRange1041_1250
          ? "1fr 340px"
          : isRange1251_1550
            ? "1fr 360px"
            : isRange1551_1940
              ? "1fr 380px"
              : "1fr 400px",
      gap: isTabletView ? "20px" : isDesktopView ? "28px" : "32px",
      alignItems: "start",
    },
    // Cards
    card: {
      backgroundColor: "#fff",
      borderRadius: isRange320_487 ? "10px" : "12px",
      padding: isRange320_487
        ? "16px"
        : isRange488_588
          ? "18px"
          : isRange589_748
            ? "20px"
            : isTabletView
              ? "22px"
              : "24px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e2e8f0",
    },
    itemCard: {
      backgroundColor: "#fff",
      borderRadius: isRange320_487 ? "10px" : "16px",
      padding: isRange320_487 ? "10px" : isRange488_588 ? "14px" : "20px",
      marginBottom: isRange320_487 ? "10px" : "16px",
      border: "1px solid #e2e8f0",
      display: "flex",
      flexDirection: isMobileView ? "column" : "row",
      gap: isRange320_487 ? "10px" : isRange488_588 ? "14px" : "24px",
      position: "relative",
    },
    // Inputs
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      marginBottom: isRange320_487 ? "12px" : "16px",
    },
    label: {
      fontSize: isRange320_487 ? "12px" : "13px",
      fontWeight: 600,
      color: "#334155",
    },
    input: {
      padding: isRange320_487 ? "8px 10px" : "10px 12px",
      borderRadius: "6px",
      border: "1px solid #cbd5e1",
      fontSize: isRange320_487 ? "13px" : "14px",
      outline: "none",
      backgroundColor: "#f8fafc",
    },
    // Summary
    summaryRow: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: isRange320_487 ? "10px" : "12px",
      fontSize: isRange320_487 ? "12px" : isRange488_588 ? "13px" : "14px",
      color: "#475569",
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: isRange320_487 ? "12px" : "16px",
      paddingTop: isRange320_487 ? "12px" : "16px",
      borderTop: "1px dashed #cbd5e1",
      fontSize: isRange320_487
        ? "16px"
        : isRange488_588
          ? "17px"
          : isTabletView
            ? "18px"
            : "20px",
      fontWeight: 800,
      color: THEME_COLOR,
    },
    // Buttons
    primaryBtn: {
      width: "100%",
      backgroundColor: THEME_COLOR,
      color: "#fff",
      padding: isRange320_487 ? "12px" : isRange488_588 ? "13px" : "14px",
      borderRadius: "8px",
      border: "none",
      fontWeight: 600,
      fontSize: isRange320_487 ? "13px" : isRange488_588 ? "14px" : "15px",
      cursor: "pointer",
      marginTop: isRange320_487 ? "16px" : "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    secondaryBtn: {
      width: "100%",
      backgroundColor: "#f1f5f9",
      color: "#475569",
      padding: isRange320_487 ? "10px" : "12px",
      borderRadius: "8px",
      border: "none",
      fontWeight: 600,
      fontSize: isRange320_487 ? "12px" : isRange488_588 ? "13px" : "14px",
      cursor: "pointer",
      marginTop: isRange320_487 ? "10px" : "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    // Quantity
    qtyControl: {
      display: "flex",
      alignItems: "center",
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      height: isRange320_487 ? "30px" : isRange488_588 ? "32px" : "36px",
    },
    qtyBtn: {
      width: isRange320_487 ? "26px" : isRange488_588 ? "28px" : "30px",
      height: "100%",
      border: "none",
      background: "transparent",
      cursor: "pointer",
      color: "#334155",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };
};

// --- UTILS ---
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    value ?? 0,
  );

const CartPage = () => {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodeError, setPincodeError] = useState("");
  const [isPincodeValid, setIsPincodeValid] = useState(false);

  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const getStateName = (stateId) =>
    states.find((s) => s.state_id === Number(stateId))?.state_title || "";
  const getDistrictName = (districtId) =>
    districts.find((d) => d.districtid === Number(districtId))
      ?.district_title || "";

  const getCityName = (cityId) =>
    cities.find((c) => c.id === Number(cityId))?.name || "";

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const responsiveStyles = getResponsiveStyles(windowWidth);

  // validate the pincode
  const validatePincode = async (stateId, districtId, pincode, cityId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/validate-code?state=${encodeURIComponent(
          stateId,
        )}&district=${encodeURIComponent(districtId)}&city=${encodeURIComponent(
          cityId,
        )}&pincode=${pincode}`,
      );

      const data = await res.json();
      const isValid = data.success || data.message === "Valid pincode";

      if (!isValid) {
        setPincodeError(
          "Please enter a valid pincode for the selected district",
        );
        setIsPincodeValid(false);
        return false;
      }

      setPincodeError("Valid pincode");
      setIsPincodeValid(true);
      return true;
    } catch (err) {
      console.error(err);
      setPincodeError("Unable to validate pincode");
      setIsPincodeValid(false);
      return false;
    }
  };

  // Load States
  useEffect(() => {
    const statesData = JSON.parse(localStorage.getItem("states") || "[]");
    setStates(statesData);
  }, []);

  // Filter Districts when State changes
  useEffect(() => {
    const districtsData = JSON.parse(localStorage.getItem("districts") || "[]");

    if (!selectedState) {
      setDistricts([]);
      return;
    }

    const filteredDistricts = districtsData.filter(
      (district) => district.state_id == selectedState,
    );

    setDistricts(filteredDistricts);
    setCities([]); // reset cities when state changes
  }, [selectedState]);

  // Filter Cities when District changes
  useEffect(() => {
    if (!selectedDistrict) {
      setCities([]);
      return;
    }

    const savedCities = localStorage.getItem("cities");

    if (savedCities) {
      const citiesData = JSON.parse(savedCities);

      const filteredCities = citiesData.filter(
        (city) => city.districtid == selectedDistrict,
      );

      setCities(filteredCities);
    }
  }, [selectedDistrict]);

  useEffect(() => {
    if (!selectedState) {
      setShippingCharge(0);
      return;
    }

    const fetchShippingFare = async () => {
      try {
        const stateName = getStateName(selectedState);

        if (!selectedState) return;

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/get-shipping-fare`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              state: stateName, // 👈 "Tamil Nadu"
            }),
          },
        );

        const data = await res.json();

        if (data.success && data.data.length > 0) {
          setShippingCharge(Number(data.data[0].base_rate));
          setDeliveryDays(data.data[0].delivery);
        } else {
          setShippingCharge(0);
        }
      } catch (error) {
        console.error("Shipping fare error:", error);
        setShippingCharge(0);
      }
    };
    fetchShippingFare();
  }, [selectedState, states]);

  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [shippingDetails, setShippingDetails] = useState({
    name: "",
    email: "",
    mobile: "",
    address: "",
    state: "", // state_id
    state_name: "", // state name
    district: "", // district_id
    district_name: "", // district name
    city: "", // city_id
    city_name: "", // city name
    pincode: "",
  });
  const [notes, setNotes] = useState("");
  const [shippingErrors, setShippingErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [addingRec, setAddingRec] = useState(null);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [deliveryDays, setDeliveryDays] = useState("");

  // --- INIT ---
  useEffect(() => {
    const initUser = async () => {
      const loggedUser = getLoggedUser();
      const storedUserData = localStorage.getItem("userData");
      let uId = null;

      if (storedUserData) {
        const u = JSON.parse(storedUserData);
        uId = u.contact || u.email;
        setShippingDetails((prev) => ({
          ...prev,
          name: u.name || "",
          email: u.email || "",
          mobile: u.mobile || u.phone || "",
          address: u.address,
        }));
      } else if (loggedUser) {
        uId = loggedUser;
      }

      setUserIdentifier(uId);
      if (uId) await fetchCartItems(uId);
    };
    initUser();
  }, []);

  const fetchCartItems = async (uid) => {
    try {
      const result = await fetchCart(uid);
      setCartItems(result?.items || []);
    } catch (err) {
      console.error(err);
    }
  };

  // cart product IDs
  const cartProductIds = cartItems.map((item) => item.productId);

  // --- RECOMMENDATIONS ---
  useEffect(() => {
    const loadRecs = async () => {
      try {
        let url = `${API_BASE_URL}/unique-list`;

        // Send all cart productIds as comma-separated list
        if (cartItems.length > 0) {
          const productIds = cartItems.map((item) => item.productId).join(",");
          url += `?product_id=${productIds}`;
        }

        const res = await axios.get(url);

        if (res.data?.data) {
          // remove products already in cart
          const filteredProducts = res.data.data.filter(
            (product) => !cartProductIds.includes(product.productid),
          );

          setRecommendedProducts(
            filteredProducts.slice(0, 4).map((p) => ({
              id: p.productid,
              name: p.productname,
              price: parseFloat(p.price),
              image: p.imageurl,
            })),
          );
        }
      } catch (e) {
        console.error(e);
      }
    };

    loadRecs();
  }, [cartItems]);

  const addRecommendation = async (product) => {
    if (!userIdentifier) {
      localStorage.clear();
      navigate("/auth");
      return;
    }

    setAddingRec(product.id);
    try {
      await axios.post(`${API_BASE_URL}/add-to-cart`, {
        user_identifier: userIdentifier,
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.price,
      });
      await fetchCartItems(userIdentifier);
    } catch (e) {
      console.error(e);
    } finally {
      setAddingRec(null);
    }
  };

  // --- CART ACTIONS ---
  const handleQtyChange = async (id, delta, total_amount) => {
    const item = cartItems.find((i) => i.id === id);
    if (!item) return;

    const newQty = Number(item.quantity) + delta;
    if (newQty < 1) return;

    // Optimistic Update
    setCartItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: newQty } : i)),
    );

    // Update total_amount based on delta
    const newTotal = total_amount + item.unitPrice * delta;

    try {
      const response = await updateCartQuantity({
        user_identifier: userIdentifier,
        product_id: item.productId,
        quantity: newQty,
        price: newTotal,
      });
    } catch (e) {
      fetchCartItems(userIdentifier); // Revert on error
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove item?")) return;
    const item = cartItems.find((i) => i.id === id);
    setCartItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await removeCartItem(userIdentifier, item.productId);
    } catch (e) {
      fetchCartItems(userIdentifier);
    }
  };

  // --- PRICE CALCULATIONS ---
  const priceSummary = useMemo(() => {
    const total = cartItems.reduce(
      (acc, item) => acc + Number(item.quantity) * Number(item.unitPrice),
      0,
    );
    const subtotal = cartItems.reduce((acc, item) => Number(item.unitPrice), 0);

    const QTY = cartItems.reduce((acc, item) => acc + Number(item.quantity), 0);

    const shippingBase = cartItems.length > 0 ? 250 : 0;

    const itemsGST = subtotal * 0.18;
    const shippingGST = shippingBase * 0.18;

    return {
      subtotal,
      shippingBase,
      itemsGST,
      shippingGST,
      total,
      QTY,
    };
  }, [cartItems]);

  // --- PAYMENT HANDLER ---
  const handlePlaceOrder = async () => {
    if (!shippingDetails.address || !shippingDetails.pincode)
      return alert("Please fill address");

    setIsSubmitting(true); // ✅ disable immediately

    try {
      const payload = {
        userIdentifier,
        ...shippingDetails,
        notes,
        shipping: shippingCharge,
        total_amount: priceSummary.total + shippingCharge,
        orders: cartItems.map((i) => ({
          proname: i.name,
          count: Number(i.quantity),
          price: Number(i.unitPrice) * Number(i.quantity),
          unit_price: Number(i.unitPrice),
          quantity: Number(i.quantity),
        })),
      };

      const { data } = await axios.post(
        `${API_BASE_URL}/add-customer-with-orders`,
        payload,
      );

      if (data.status !== "success") throw new Error("Server Error");

      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) throw new Error("Razorpay failed to load");

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: data.amount * 100,
        currency: "INR",
        name: "CGRMART",
        description: "Order Payment",
        order_id: data.razorpay_order_id,
        prefill: {
          name: shippingDetails.name,
          email: shippingDetails.email,
          contact: shippingDetails.mobile,
        },
        theme: { color: THEME_COLOR },
        // ✅ SUCCESS HANDLER
        handler: async function (response) {
          try {
            await axios.post(`${API_BASE_URL}/update-payment-status`, {
              razorpay_orderid: data.razorpay_order_id,
              razorpay_paymentid: response.razorpay_payment_id,
              payment_status: "SUCCESS",
            });

            // 🚀 Navigate (button stays disabled)
            navigate("/order-success", {
              state: {
                orderData: {
                  orderId: data.razorpay_order_id,
                  paymentId: response.razorpay_payment_id,
                  items: cartItems,
                  shipping: shippingDetails,
                  summary: priceSummary,
                  shippingCharge: shippingCharge,
                  date: new Date().toLocaleDateString(),
                },
              },
            });
          } catch (e) {
            alert("Payment verification failed");
            setIsSubmitting(false); // ❗ re-enable only on failure
          }
        },
        // ✅ HANDLE MODAL CLOSE (user cancels)
        modal: {
          ondismiss: function () {
            // console.log("Payment popup closed");
            setIsSubmitting(false); // ❗ re-enable if user cancels
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Something went wrong with the order.");
      setIsSubmitting(false); // ❗ re-enable on error
    }
  };
  // --- RENDERERS ---

  const renderCartStep = () => {
    // Match global breakpoint ranges
    const isSmallMobile = windowWidth >= 320 && windowWidth < 488; // 320–487
    const isMobile = windowWidth >= 488 && windowWidth < 589; // 488–588
    const isMobileView = windowWidth < 749; // 320–748

    return (
      <>
        <div
          style={{
            display: "flex",
            flexDirection: isMobileView ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobileView ? "flex-start" : "center",
            gap: isMobileView ? "10px" : "0",
            marginBottom: isSmallMobile ? "10px" : isMobile ? "12px" : "15px",
          }}
          id="CardsList"
        >
          <h2
            style={{
              margin: 0,
              fontSize: isSmallMobile ? "14px" : isMobile ? "16px" : "20px",
              fontWeight: 700,
            }}
          >
            Cart Items ({cartItems.length})
          </h2>
          <span
            style={{
              background: "#ecfdf5",
              color: "#10b981",
              padding: isSmallMobile ? "4px 8px" : "5px 10px",
              borderRadius: "15px",
              fontSize: isSmallMobile ? "10px" : isMobile ? "11px" : "12px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <FaShieldAlt /> Trade Assurance
          </span>
        </div>

        {cartItems.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              background: "#fff",
              borderRadius: "12px",
            }}
          >
            <p>Your cart is empty.</p>
            <button
              style={responsiveStyles.secondaryBtn}
              onClick={() => navigate("/")}
            >
              Browse Products
            </button>
          </div>
        ) : (
          cartItems.map((item) => {
            const isItemSmallMobile = windowWidth >= 320 && windowWidth < 488;
            const isItemMobile = windowWidth >= 488 && windowWidth < 589;
            const isItemMobileView = windowWidth < 749;

            return (
              <div key={item.id} style={responsiveStyles.itemCard}>
                <img
                  src={item.image || DEFAULT_IMAGE}
                  style={{
                    width: isItemSmallMobile
                      ? "100%"
                      : isItemMobile
                        ? "90px"
                        : isItemMobileView
                          ? "100px"
                          : "80px",
                    height: isItemSmallMobile
                      ? "150px"
                      : isItemMobile
                        ? "90px"
                        : isItemMobileView
                          ? "100px"
                          : "80px",
                    maxWidth: isItemSmallMobile ? "100%" : "90px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    alignSelf: isItemSmallMobile ? "center" : "flex-start",
                  }}
                  alt=""
                  onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                />
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      margin: "0 0 5px 0",
                      fontSize: isItemSmallMobile
                        ? "13px"
                        : isItemMobile
                          ? "14px"
                          : "16px",
                      fontWeight: 600,
                      lineHeight: "1.3",
                    }}
                  >
                    {item.name}
                  </h4>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: isItemSmallMobile ? "column" : "row",
                      justifyContent: "space-between",
                      alignItems: isItemSmallMobile ? "flex-start" : "flex-end",
                      gap: isItemSmallMobile ? "12px" : "0",
                      marginTop: "10px",
                    }}
                  >
                    <div style={responsiveStyles.qtyControl}>
                      <button
                        style={responsiveStyles.qtyBtn}
                        onClick={() =>
                          handleQtyChange(item.id, -1, priceSummary.total)
                        }
                      >
                        <FaMinus size={isItemSmallMobile ? 8 : 10} />
                      </button>
                      <span
                        style={{
                          padding: "0 8px",
                          fontSize: isItemSmallMobile
                            ? "12px"
                            : isItemMobile
                              ? "13px"
                              : "14px",
                          fontWeight: "600",
                        }}
                      >
                        {item.quantity}
                      </span>
                      <button
                        style={responsiveStyles.qtyBtn}
                        onClick={() =>
                          handleQtyChange(item.id, 1, priceSummary.total)
                        }
                      >
                        <FaPlus size={isItemSmallMobile ? 8 : 10} />
                      </button>
                    </div>
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: isItemSmallMobile
                          ? "13px"
                          : isItemMobile
                            ? "14px"
                            : "16px",
                        color: "#0f172a",
                      }}
                    >
                      {formatCurrency(item.unitPrice * item.quantity)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(item.id)}
                  style={{
                    position: "absolute",
                    top: isSmallMobile ? "6px" : "8px",
                    right: isSmallMobile ? "6px" : "8px",
                    background: "rgba(239, 68, 68, 0.1)",
                    border: "none",
                    color: "#ef4444",
                    cursor: "pointer",
                    fontSize: isSmallMobile ? "12px" : "14px",
                    width: isSmallMobile ? "28px" : "32px",
                    height: isSmallMobile ? "28px" : "32px",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTrash />
                </button>
              </div>
            );
          })
        )}

        {/* Recommendations */}
        {recommendedProducts.length > 0 && (
          <div style={{ marginTop: windowWidth < 488 ? "20px" : "30px" }}>
            <h4
              style={{
                fontSize:
                  windowWidth < 488
                    ? "16px"
                    : windowWidth < 589
                      ? "17px"
                      : "18px",
              }}
            >
              You might also like
            </h4>
            <div
              style={{
                display: "flex",
                gap: windowWidth < 488 ? "10px" : "15px",
                overflowX: "auto",
                paddingBottom: "10px",
              }}
            >
              {recommendedProducts.map((rec) => {
                const isSmallMobileRec =
                  windowWidth >= 320 && windowWidth < 488;
                return (
                  <div
                    key={rec.id}
                    style={{
                      minWidth: isSmallMobileRec ? "120px" : "140px",
                      background: "#fff",
                      padding: isSmallMobileRec ? "8px" : "10px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <img
                      src={rec.image || DEFAULT_IMAGE}
                      style={{
                        width: "100%",
                        height: isSmallMobileRec ? "60px" : "80px",
                        objectFit: "contain",
                      }}
                      alt=""
                    />
                    <p
                      style={{
                        fontSize: isSmallMobileRec ? "11px" : "12px",
                        fontWeight: "600",
                        margin: "5px 0",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {rec.name}
                    </p>
                    <p
                      style={{
                        fontSize: isSmallMobileRec ? "11px" : "12px",
                        color: THEME_COLOR,
                        fontWeight: "700",
                      }}
                    >
                      {formatCurrency(rec.price)}
                    </p>
                    <button
                      disabled={addingRec === rec.id}
                      onClick={() => addRecommendation(rec)}
                      style={{
                        width: "100%",
                        padding: isSmallMobileRec ? "4px" : "5px",
                        marginTop: "5px",
                        background: THEME_COLOR,
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: isSmallMobileRec ? "10px" : "11px",
                      }}
                    >
                      {addingRec === rec.id ? "Adding..." : "+ Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderShippingStep = () => {
    const isSmallMobile = windowWidth >= 320 && windowWidth < 488; // 320–487
    const isMobile = windowWidth >= 488 && windowWidth < 589; // 488–588
    const isMobileView = windowWidth < 749; // 320–748

    return (
      <div style={responsiveStyles.card}>
        <h2
          style={{
            marginTop: 0,
            fontSize: isSmallMobile ? "18px" : isMobile ? "20px" : "22px",
          }}
        >
          Shipping Details
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
            gap: isSmallMobile ? "12px" : "15px",
          }}
        >
          <div style={responsiveStyles.inputGroup}>
            <label style={responsiveStyles.label}>Full Name</label>
            <input
              style={responsiveStyles.input}
              value={shippingDetails.name}
              onChange={(e) =>
                setShippingDetails({ ...shippingDetails, name: e.target.value })
              }
            />
          </div>
          <div style={responsiveStyles.inputGroup}>
            <label style={responsiveStyles.label}>Mobile</label>
            <input
              style={responsiveStyles.input}
              value={shippingDetails.mobile}
              onChange={(e) =>
                setShippingDetails({
                  ...shippingDetails,
                  mobile: e.target.value,
                })
              }
            />
          </div>
        </div>

        <div style={responsiveStyles.inputGroup}>
          <label style={responsiveStyles.label}>Email</label>
          <input
            style={responsiveStyles.input}
            value={shippingDetails.email}
            onChange={(e) =>
              setShippingDetails({ ...shippingDetails, email: e.target.value })
            }
          />
        </div>

        <div style={responsiveStyles.inputGroup}>
          <label style={responsiveStyles.label}>Address</label>
          <textarea
            style={{ ...responsiveStyles.input, minHeight: "60px" }}
            value={shippingDetails.address}
            onChange={(e) =>
              setShippingDetails({
                ...shippingDetails,
                address: e.target.value,
              })
            }
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
            gap: isSmallMobile ? "12px" : "15px",
          }}
        >
          <div style={responsiveStyles.inputGroup}>
            <label style={responsiveStyles.label}>State</label>
            <select
              value={selectedState}
              style={responsiveStyles.input}
              onChange={(e) => {
                const val = e.target.value;
                const name = getStateName(val); // ✅ FIX

                setSelectedState(val);
                setShippingDetails((prev) => ({
                  ...prev,
                  state: val, // ID
                  state_name: name, // NAME
                  district: "",
                  district_name: "",
                  city: "",
                  city_name: "",
                }));

                setSelectedDistrict("");
                setCities([]);
              }}
            >
              <option value="">Select State</option>
              {states.map((state, index) => (
                <option key={state.state_id || index} value={state.state_id}>
                  {state.state_title}
                </option>
              ))}
            </select>
          </div>

          <div style={responsiveStyles.inputGroup}>
            <label style={responsiveStyles.label}>District</label>
            <select
              value={selectedDistrict}
              style={responsiveStyles.input}
              onChange={(e) => {
                const val = e.target.value;
                const name = getDistrictName(val); // ✅ FIX

                setSelectedDistrict(val);
                setShippingDetails((prev) => ({
                  ...prev,
                  district: val, // ID
                  district_name: name, // NAME
                  city: "",
                  city_name: "",
                }));

                setSelectedCity("");
              }}
              disabled={!districts.length}
            >
              <option value="">Select District</option>
              {districts.map((district, index) => (
                <option
                  key={district.districtid || index}
                  value={district.districtid}
                >
                  {district.district_title}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
            gap: isSmallMobile ? "12px" : "15px",
          }}
        >
          <div style={responsiveStyles.inputGroup}>
            <label style={responsiveStyles.label}>City</label>
            <select
              value={selectedCity}
              style={responsiveStyles.input}
              onChange={(e) => {
                const val = e.target.value;
                const name = getCityName(val); // ✅ FIX

                setSelectedCity(val);
                setShippingDetails((prev) => ({
                  ...prev,
                  city: val, // ID
                  city_name: name, // NAME
                }));
              }}
              disabled={!cities.length}
            >
              <option value="">Select City</option>
              {cities.map((city, index) => (
                <option key={city.id || index} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
          <div style={responsiveStyles.inputGroup}>
            <label style={responsiveStyles.label}>Pincode</label>

            <input
              style={{
                ...responsiveStyles.input,
                borderColor: isPincodeValid
                  ? "#10b981"
                  : pincodeError
                    ? "#ef4444"
                    : "#cbd5e1",
              }}
              value={shippingDetails.pincode}
              onChange={async (e) => {
                const value = e.target.value.replace(/\D/g, ""); // numbers only
                setShippingDetails({ ...shippingDetails, pincode: value });

                // Reset states while typing
                setIsPincodeValid(false);
                setPincodeError("");

                // Validate only when 6 digits + district selected
                if (value.length === 6 && selectedDistrict) {
                  const stateName = getStateName(selectedState);
                  const districtName = getDistrictName(selectedDistrict);
                  const cityName = getCityName(selectedCity);
                  // console.log(stateName, districtName, value, cityName);
                  await validatePincode(
                    stateName,
                    districtName,
                    value,
                    cityName,
                  );
                }
              }}
            />

            {/* Error Message */}
            {pincodeError && !isPincodeValid && (
              <span style={{ color: "#ef4444", fontSize: "12px" }}>
                {pincodeError}
              </span>
            )}

            {/* Success Message */}
            {isPincodeValid && shippingDetails.pincode.length === 6 && (
              <span style={{ color: "#10b981", fontSize: "12px" }}>
                ✓ Pincode is valid for the selected district
              </span>
            )}
          </div>
        </div>
        <div style={responsiveStyles.inputGroup}>
          <label style={responsiveStyles.label}>Order Notes (Optional)</label>
          <textarea
            style={{ ...responsiveStyles.input, minHeight: "50px" }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    );
  };

  const renderPaymentStep = () => {
    const isSmallMobile = windowWidth >= 320 && windowWidth < 488; // 320–487
    const isMobile = windowWidth >= 488 && windowWidth < 589; // 488–588

    return (
      <div style={responsiveStyles.card}>
        <h2
          style={{
            marginTop: 0,
            fontSize: isSmallMobile ? "18px" : isMobile ? "20px" : "22px",
          }}
        >
          Review & Pay
        </h2>
        <div
          style={{
            background: "#F8FAFC",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "15px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span
              style={{ fontSize: "12px", fontWeight: "700", color: "#64748B" }}
            >
              SHIPPING TO
            </span>
            <span
              style={{
                fontSize: "12px",
                color: THEME_COLOR,
                cursor: "pointer",
              }}
              onClick={() => setCurrentStep(2)}
            >
              <FaEdit /> Edit
            </span>
          </div>
          <p style={{ fontSize: "14px", fontWeight: "600", margin: "5px 0" }}>
            {shippingDetails.name}
          </p>
          <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>
            {shippingDetails.address}, {shippingDetails.city_name} -{" "}
            {shippingDetails.pincode}
          </p>
          <p style={{ fontSize: "13px", color: "#475569", margin: "2px 0" }}>
            {shippingDetails.state_name}
          </p>
          <p style={{ fontSize: "13px", color: "#475569", margin: "2px 0" }}>
            {shippingDetails.mobile}
          </p>
        </div>

        <div
          style={{
            background: "#F8FAFC",
            padding: "15px",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#64748B",
              marginBottom: "10px",
            }}
          >
            ITEMS ({cartItems.length})
          </div>
          {cartItems.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "13px",
                marginBottom: "5px",
              }}
            >
              <span>
                {item.quantity} x {item.name}
              </span>
              <span style={{ fontWeight: "600" }}>
                {formatCurrency(item.unitPrice * item.quantity)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOrderSummary = () => {
    const isSmallMobile = windowWidth >= 320 && windowWidth < 488; // 320–487
    const isMobile = windowWidth >= 488 && windowWidth < 589; // 488–588

    return (
      <div style={responsiveStyles.card}>
        <h3
          style={{
            margin: "0 0 20px 0",
            fontSize: isSmallMobile ? "16px" : isMobile ? "18px" : "20px",
          }}
        >
          Order Summary
        </h3>

        <div style={responsiveStyles.summaryRow}>
          <span>Subtotal (GST)</span>
          <span style={{ fontWeight: 600, color: "#0f172a" }}>
            {formatCurrency(priceSummary.total)}
          </span>
        </div>

        <div style={responsiveStyles.summaryRow}>
          <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            Quantity
          </span>
          <span style={{ fontWeight: 600, color: "#0f172a" }}>
            {priceSummary.QTY}
          </span>
        </div>

        {currentStep != 1 && (
          <>
            <div style={responsiveStyles.summaryRow}>
              <span
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <FaTruck size={isSmallMobile ? 10 : 12} /> Shipping Charges
              </span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>
                {formatCurrency(shippingCharge)}
              </span>
            </div>
          </>
        )}

        <div style={responsiveStyles.totalRow}>
          <span>Total Amount</span>
          <span>
            {formatCurrency(
              Number(priceSummary.total) +
                (currentStep >= 2 ? Number(shippingCharge || 0) : 0),
            )}
          </span>
        </div>

        {/* Action Buttons based on Step */}
        {currentStep === 1 && (
          <button
            style={responsiveStyles.primaryBtn}
            onClick={() => {
              if (cartItems.length === 0) return alert("Cart is empty");
              setCurrentStep(2);
            }}
          >
            Proceed to Shipping{" "}
            <FaArrowLeft style={{ transform: "rotate(180deg)" }} />
          </button>
        )}

        {currentStep === 2 && (
          <>
            <button
              style={responsiveStyles.primaryBtn}
              onClick={() => {
                if (!isPincodeValid) {
                  alert(
                    "Please enter a valid pincode for the selected district",
                  );
                  return;
                }
                setCurrentStep(3);
              }}
            >
              Continue to Payment{" "}
              <FaArrowLeft style={{ transform: "rotate(180deg)" }} />
            </button>

            <button
              style={responsiveStyles.secondaryBtn}
              onClick={() => setCurrentStep(1)}
            >
              Back to Cart
            </button>
          </>
        )}

        {currentStep === 3 && (
          <>
            <button
              style={{ ...responsiveStyles.primaryBtn, background: "#10b981" }}
              onClick={handlePlaceOrder}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing..."
                : `Pay ${formatCurrency(
                    priceSummary.total + Number(shippingCharge),
                  )}`}
            </button>
            <button
              style={responsiveStyles.secondaryBtn}
              onClick={() => setCurrentStep(2)}
              disabled={isSubmitting}
            >
              Back to Shipping
            </button>
          </>
        )}

        <div
          style={{
            marginTop: "20px",
            fontSize: isSmallMobile ? "10px" : "11px",
            color: "#94a3b8",
            display: "flex",
            gap: "5px",
            lineHeight: "1.4",
          }}
        >
          <FaInfoCircle /> Prices are inclusive of all applicable taxes.
          Shipping calculated at checkout.
        </div>
      </div>
    );
  };

  // --- MAIN UI ---
  return (
    <>
      <Helmet>
        <title>Shopping Cart | CGRMART</title>

        <meta
          name="description"
          content="Review your cart items, update quantities, and proceed to secure checkout at CGRMART. Fast delivery across India."
        />

        <meta
          name="keywords"
          content="shopping cart, checkout, buy online, EV products, CGRMART, India ecommerce"
        />
        
        <meta name="author" content="CGRMART" />

        {/* Open Graph (Facebook / LinkedIn) */}
        <meta property="og:title" content="Shopping Cart | CGRMART" />
        <meta
          property="og:description"
          content="Check your cart and complete your purchase securely."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cgrmart.com/cart" />

        {/* Twitter SEO */}
        <meta name="twitter:title" content="Shopping Cart | CGRMART" />
        <meta
          name="twitter:description"
          content="Secure checkout and fast delivery across India."
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://cgrmart.com/cart" />
      </Helmet>

      {/* Your existing UI */}
      <Header />
      <div style={responsiveStyles.page}>
        <div style={responsiveStyles.container}>
          {/* Page Header */}
          <div style={responsiveStyles.headerRow} id="headerRow">
            <h1 style={responsiveStyles.title}>Shopping Cart</h1>

            {/* Stepper */}
            <div style={responsiveStyles.stepperContainer}>
              <div
                style={responsiveStyles.stepItem(
                  currentStep === 1 ? "active" : "completed",
                )}
              >
                {currentStep > 1 ? <FaCheck /> : ""} Cart
              </div>
              <div style={{ color: "#cbd5e1" }}>&gt;</div>
              <div
                style={responsiveStyles.stepItem(
                  currentStep === 2
                    ? "active"
                    : currentStep > 2
                      ? "completed"
                      : "pending",
                )}
              >
                {currentStep > 2 ? <FaCheck /> : ""} Shipping
              </div>
              <div style={{ color: "#cbd5e1" }}>&gt;</div>
              <div
                style={responsiveStyles.stepItem(
                  currentStep === 3 ? "active" : "pending",
                )}
              >
                Payment
              </div>
            </div>

            <div
              onClick={() => {
                navigate("/");
                window.location.reload();
              }}
              style={responsiveStyles.backLink}
            >
              <FaArrowLeft /> Continue Shopping
            </div>
          </div>

          {/* Content Grid */}
          <div style={responsiveStyles.layout}>
            <div>
              {currentStep === 1 && renderCartStep()}
              {currentStep === 2 && renderShippingStep()}
              {currentStep === 3 && renderPaymentStep()}
            </div>

            {/* Sticky Summary */}
            <div
              style={{
                position: windowWidth < 749 ? "static" : "sticky",
                top:
                  windowWidth < 488
                    ? "80px"
                    : windowWidth < 589
                      ? "85px"
                      : "100px",
                marginTop:
                  windowWidth < 749
                    ? windowWidth < 488
                      ? "16px"
                      : "20px"
                    : "0",
              }}
            >
              {renderOrderSummary()}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CartPage;
